import React from 'react';
import MsDyn365 from '@msdyn365-commerce/core';
import {
    useFloating,
    offset as fuiOffset,
    flip,
    shift,
    size,
    autoUpdate,
    useDismiss,
    useRole,
    useInteractions,
    FloatingPortal,
    FloatingFocusManager,
    Placement
} from '@floating-ui/react';

// Using Floating UI to portal to document.body, avoid overflow clipping, and
// auto-position with flip/shift. Inline comments mark key bits.

type LocalDropdownProps = {
    trigger: (p: { ref: React.Ref<HTMLElement>; onClick: () => void; 'aria-expanded': boolean }) => React.ReactNode;
    children: (close: () => void) => React.ReactNode;
    matchWidth?: boolean;
    offset?: number;
    placement?: Placement;
    keepChildrenMounted?: boolean;
};

export function LocalDropdown({
    trigger,
    children,
    matchWidth = true,
    offset = 4,
    placement = 'bottom-end',
    keepChildrenMounted = false
}: LocalDropdownProps) {
    const [open, setOpen] = React.useState(false);

    // Floating UI core: compute styles + keep updated on scroll/resize/layout
    const { refs, floatingStyles, context } = useFloating({
        open,
        onOpenChange: setOpen,
        placement,
        middleware: [
            fuiOffset(offset),
            flip({ padding: 8 }), // flip above if there's not enough room below
            shift({ padding: 8 }), // nudge inside viewport if overflowing horizontally
            size({
                // Ensure a minimum width equal to the reference (or 400px),
                // and cap height to available viewport space with scroll.
                apply({ rects, availableHeight, elements, availableWidth }) {
                    if (matchWidth) {
                        const w = Math.max(rects.reference.width, 400);
                        Object.assign(elements.floating.style, { minWidth: `${w}px` });
                    } else {
                        const minWidth = window.innerWidth <= 610 ? Math.max(rects.reference.width, 400) : 400;

                        Object.assign(elements.floating.style, { width: '100%', maxWidth: `${Math.min(minWidth, Math.floor(availableWidth - 16))}px` });
                    }
                    /* Object.assign(elements.floating.style, {
                        maxHeight: `${Math.max(200, Math.floor(availableHeight - 8))}px`
                    }); */
                }
            })
        ],
        whileElementsMounted: autoUpdate
    });

    // Interactions: click to toggle, Esc/outside click to dismiss, ARIA role=menu
    const dismiss = useDismiss(context, {
        escapeKey: true,
        outsidePressEvent: 'pointerdown',
        outsidePress: e => {
            const t = e.target as Node;
            return !refs.floating.current?.contains(t) && !refs.domReference.current?.contains(t);
        }
    });
    const role = useRole(context, { role: 'menu' });
    const { getReferenceProps, getFloatingProps } = useInteractions([dismiss, role]);

    // Build trigger and merge reference props ON THE SAME ELEMENT as the ref
    const rawTrigger = trigger({
        ref: (refs.setReference as unknown) as React.Ref<HTMLElement>,
        onClick: () => setOpen(o => !o),
        'aria-expanded': open
    });

    const triggerNode = React.isValidElement(rawTrigger)
        ? React.cloneElement(
              rawTrigger as any,
              getReferenceProps({
                  onClick: () => setOpen(o => !o),
                  'aria-expanded': open
              })
          )
        : rawTrigger;

    return (
        <>
            {triggerNode}

            {(open || keepChildrenMounted) && MsDyn365.isBrowser && (
                <FloatingPortal>
                    {/* Focus manager keeps focus trapped inside while open (non-modal keeps page accessible) */}
                    <FloatingFocusManager context={context} modal={false}>
                        <div
                            ref={refs.setFloating}
                            style={{
                                ...floatingStyles,
                                display: keepChildrenMounted && !open ? 'none' : 'block',
                                zIndex: 1000
                            }}
                            {...getFloatingProps()} onPointerDown={(e) => e.stopPropagation()}
                        >
                            {(open || keepChildrenMounted) && children(() => setOpen(false))}
                        </div>
                    </FloatingFocusManager>
                </FloatingPortal>
            )}
        </>
    );
}
